import React, { useEffect, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Group, Vector3 } from 'three';
import Sun from './Sun';
import Planet from './Planet';
import SimpleBlackHole from './SimpleBlackHole';
import { Project } from '../types/Project';
import { projectsData } from '../data/projects';

interface GalaxyProps {
  focusedProjectId: string | null;
  onPlanetFocus: (project: Project, position: Vector3, scale: number) => void;
  onPlanetDetails: (project: Project) => void;
  onPlanetFocusMove: (project: Project, position: Vector3, scale: number) => void;
  onSunClick: () => void;
  activeDetailProjectId?: string | null;
  revealProgress?: number; // 0 -> invisible, 1 -> full
  rotationSpeedMultiplier?: number; // global multiplier from slider (0..2)
  activeCategory?: string | null;
}

interface InternalProps extends GalaxyProps {
  paused?: boolean;
}

const Galaxy: React.FC<InternalProps> = ({ focusedProjectId, onPlanetFocus, onPlanetDetails, onPlanetFocusMove, onSunClick, activeDetailProjectId, revealProgress = 1, paused = false, rotationSpeedMultiplier = 1, activeCategory = null }) => {
  const galaxyRef = useRef<Group>(null);
  const angleRef = useRef(projectsData.map(() => Math.random() * Math.PI * 2));
  const angles = angleRef.current;
  const isDetailMode = Boolean(activeDetailProjectId);

  // Stagger planet reveal when exiting detail mode to avoid a single heavy frame
  const [visibleCount, setVisibleCount] = useState<number>(projectsData.length);
  // Animation state ref to avoid excessive re-renders
  const visibleCountAnim = useRef(projectsData.length);

  useFrame((state, delta) => {
    // Rotation logic
    if (galaxyRef.current && !paused) {
      const baseSpeed = isDetailMode ? 0 : (focusedProjectId ? 0.0001 : 0.0002);
      galaxyRef.current.rotation.y += baseSpeed * rotationSpeedMultiplier;
    }

    // Visibility animation logic
    const target = isDetailMode ? 0 : projectsData.length;
    const isSunDetail = activeDetailProjectId === 'sun-profile';

    // Snap instantly if entering detail mode for a planet (not Sun)
    if (isDetailMode && !isSunDetail) {
      visibleCountAnim.current = 0;
      if (visibleCount !== 0) {
        setVisibleCount(0);
      }
    } else if (Math.abs(visibleCountAnim.current - target) > 0.01) {
      const speed = 25 * delta; // Speed of disappearance/reappearance
      if (visibleCountAnim.current < target) {
        visibleCountAnim.current = Math.min(target, visibleCountAnim.current + speed);
      } else {
        visibleCountAnim.current = Math.max(target, visibleCountAnim.current - speed);
      }

      const discrete = Math.round(visibleCountAnim.current);
      if (discrete !== visibleCount) {
        setVisibleCount(discrete);
      }
    }
  });

  // Compute sequential reveal by outer-most orbits first, sun last.
  const ranks = React.useMemo(() => {
    const sorted = [...projectsData]
      .map((p) => ({ id: p.id, r: p.orbitRadius }))
      .sort((a, b) => b.r - a.r); // outer first
    const map = new Map<string, number>();
    sorted.forEach((entry, i) => map.set(entry.id, i));
    return { map, count: sorted.length };
  }, []);

  // Weighted staging: outer orbits quick, inner slower, sun slowest
  const smooth = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));
  const weights = React.useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < ranks.count; i++) {
      // 0..1 across ranks (outer=0, inner=1)
      const t = ranks.count > 1 ? i / (ranks.count - 1) : 0;
      // Outer smaller weight (faster), inner bigger (slower)
      arr.push(0.6 + t * 0.9);
    }
    return arr;
  }, [ranks.count]);
  const sunWeight = 1.4;
  const cumWeights = React.useMemo(() => {
    const cw: number[] = [0];
    for (let i = 0; i < weights.length; i++) cw.push(cw[i] + weights[i]);
    return cw; // length = count+1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weights]); // Adding weights to deps to satisfy linter, though it's derived from ranks.count
  const planetsTotalWeight = cumWeights[cumWeights.length - 1];
  const totalWeight = planetsTotalWeight + sunWeight;
  const s = Math.max(0, Math.min(1, revealProgress)) * totalWeight;

  return (
    <group ref={galaxyRef}>
      {/* Trou noir simple en arrière-plan */}
      {/* <SimpleBlackHole position={[0, 0, -300]} scale={15} /> */}

      {projectsData.map((project, index) => {
        // Show if it's the active project OR if it's within the visible count
        const isVisible = (activeDetailProjectId === project.id) || (index < visibleCount);
        const rank = ranks.map.get(project.id) ?? 0;
        // Progress inside this planet's weighted stage
        const planetAlpha = smooth(Math.max(0, Math.min(1, (s - cumWeights[rank]) / weights[rank])));
        return (
          <group key={project.id} visible={isVisible}>
            <Planet
              project={project}
              isFocused={focusedProjectId === project.id}
              onFocus={onPlanetFocus}
              onRequestDetails={onPlanetDetails}
              onFocusMove={onPlanetFocusMove}
              initialAngle={angles[index]}
              activeDetailProjectId={activeDetailProjectId}
              globalReveal={planetAlpha}
              paused={paused}
              speedMultiplier={rotationSpeedMultiplier}
              activeCategory={activeCategory}
            />
          </group>
        );
      })}

      {/* Soleil rendu après les planètes pour éviter les conflits de clics */}
      <Sun
        onClick={isDetailMode ? () => { } : onSunClick}
        fadeAlpha={smooth(Math.max(0, Math.min(1, (s - planetsTotalWeight) / sunWeight))) * (isDetailMode && activeDetailProjectId !== 'sun-profile' ? 0 : 1)}
      />
    </group>
  );
};

export default Galaxy;